import React, {useEffect, useState} from "react";
import {useDispatch} from "react-redux";
import {
    Button,
    CardActions,
    CardContent,
    TextField,
    CardHeader,
    Grid,
    Alert,
} from "@mui/material";
import {DatePicker} from "@mui/x-date-pickers/DatePicker";
import {LocalizationProvider} from "@mui/x-date-pickers/LocalizationProvider";
import {AdapterDayjs} from "@mui/x-date-pickers/AdapterDayjs";
import {setModal} from "../../../../../Common/App";
import {DialogCard, Loader} from "../../../../../../Components";
import {addHoliday, updateHoliday} from "../../../Redux/Action";
import dayjs from 'dayjs';

export const HolidayModal = ({item}) => {

    const dispatch = useDispatch();
    const [disabled] = useState(false);
    const [errors, setErrors] = useState({});
    const [message, setMessage] = useState('');
    const [isLoading, setIsLoading] = useState(false);
    const [data, setData] = useState({
        id: null,
        holiday_name: '',
        start_date: dayjs(),
        end_date: dayjs(),
        description: '',
    });

    useEffect(() => {
        if (item) {
            const holidayDate = item.holiday_date ? dayjs(item.holiday_date) : dayjs();
            setData({
                id: item.id,
                holiday_name: item.holiday_name || '',
                start_date: holidayDate,
                end_date: holidayDate,
                description: item.description || '',
            });
        }
    }, [item]);

    const handleSaveOrUpdate = async () => {
        const currentYear = dayjs().year();
        const startYear = data.start_date.year();
        const endYear = data.end_date.year();
        
        if (!data.holiday_name.trim()) {
            setErrors({ holiday_name: 'Holiday name is required' });
            setMessage('Holiday name is required');
            return;
        }

        if (!data.start_date || !data.end_date) {
            setErrors({ start_date: 'Start date and end date are required' });
            setMessage('Please select both start and end dates');
            return;
        }

        if (data.end_date.isBefore(data.start_date)) {
            setErrors({ end_date: 'End date must be after start date' });
            setMessage('End date must be after start date');
            return;
        }

        if (startYear !== currentYear || endYear !== currentYear) {
            setErrors({ start_date: 'Only current year holidays can be added' });
            setMessage('Holiday dates must be in the current year');
            return;
        }

        setIsLoading(true);
        const payload = {
            ...data,
            start_date: data.start_date.format('YYYY-MM-DD'),
            end_date: data.end_date.format('YYYY-MM-DD'),
        };
        
        if (data.id) {
            await updateHoliday({ dispatch, data: payload }).then((res) => {
                setIsLoading(false);
                if (res.success) {
                    dispatch(setModal({component:null, open:false}));
                } else {
                    setErrors(res.errors || {});
                    setMessage(res.message || '');
                }
            });
        } else {
            await addHoliday({ dispatch, data: payload }).then((res) => {
                setIsLoading(false);
                if (res.success) {
                    dispatch(setModal({component:null, open:false}));
                } else {
                    setErrors(res.errors || {});
                    setMessage(res.message || '');
                }
            });
        }
    };

    const handleChange = (e) => {
        setErrors({});
        setMessage('');
        const { name, value, checked, type } = e.target;
        setData((prev) => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value,
        }));
    };

    const handleStartDateChange = (date) => {
        setErrors({});
        setMessage('');
        setData((prev) => {
            const newEndDate = prev.end_date && prev.end_date.isBefore(date) ? date : prev.end_date;
            return {
                ...prev,
                start_date: date,
                end_date: newEndDate,
            };
        });
    };

    const handleEndDateChange = (date) => {
        setErrors({});
        setMessage('');
        setData((prev) => ({
            ...prev,
            end_date: date,
        }));
    };

    return (
        <DialogCard>
            <CardHeader
                title={data.id ? "Edit Holiday" : "Add Holiday"}
                avatar={<Loader isLoading={isLoading} type={"linear"} />}
            />
            <CardContent>
                {message && (
                    <Alert severity="error" sx={{mb: 2}}>
                        {message}
                    </Alert>
                )}
                <Grid container spacing={2}>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Holiday Name"
                            name="holiday_name"
                            value={data.holiday_name}
                            onChange={handleChange}
                            error={!!errors.holiday_name}
                            helperText={errors.holiday_name}
                            disabled={disabled}
                            required
                        />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="Start Date"
                                value={data.start_date}
                                onChange={handleStartDateChange}
                                disabled={disabled}
                                views={['year', 'month', 'day']}
                                minDate={dayjs().startOf('year')}
                                maxDate={dayjs().endOf('year')}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!errors.start_date,
                                        helperText: errors.start_date,
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <LocalizationProvider dateAdapter={AdapterDayjs}>
                            <DatePicker
                                label="End Date"
                                value={data.end_date}
                                onChange={handleEndDateChange}
                                disabled={disabled}
                                views={['year', 'month', 'day']}
                                minDate={data.start_date || dayjs().startOf('year')}
                                maxDate={dayjs().endOf('year')}
                                slotProps={{
                                    textField: {
                                        fullWidth: true,
                                        error: !!errors.end_date,
                                        helperText: errors.end_date,
                                    }
                                }}
                            />
                        </LocalizationProvider>
                    </Grid>
                    <Grid item xs={12}>
                        <TextField
                            fullWidth
                            label="Description"
                            name="description"
                            value={data.description}
                            onChange={handleChange}
                            error={!!errors.description}
                            helperText={errors.description}
                            disabled={disabled}
                            multiline
                            rows={3}
                        />
                    </Grid>
                </Grid>
            </CardContent>
            <CardActions sx={{ justifyContent: 'flex-end' }}>
                <Button
                    variant="outlined"
                    onClick={() => dispatch(setModal({component:null, open:false}))}
                    disabled={isLoading}
                >
                    Cancel
                </Button>
                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleSaveOrUpdate}
                    disabled={isLoading || disabled}
                >
                    {isLoading ? 'Saving...' : (data.id ? 'Update' : 'Add')}
                </Button>
            </CardActions>
        </DialogCard>
    );
};
